import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  favouriteSpecialistsListSelector,
  favouriteSpecialistsLoadingSelector,
} from '../redux/favouriteSpecialits/selector';
import { specialistsSelector } from '../redux/specialists/selector';
import { addSpecialistToFavourite, removeSpecialistFromFavourite } from '../redux/favouriteSpecialits/thunk';
import { ImpactStyle } from '@capacitor/haptics';
import { userSelector } from '../redux/user/selector';
import { ISpecialist } from 'src/redux/specialists/types';
import { Specialist } from '@mindly/ui-components';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

export const useFavouriteSpecialist = () => {
  const dispatch = useDispatch();

  const currentUser = useSelector(userSelector);

  const favouriteSpecialistList = useSelector(favouriteSpecialistsListSelector);
  const specialistList = useSelector(specialistsSelector);
  const favouriteSpecialistListStatus = useSelector(favouriteSpecialistsLoadingSelector);

  const debouncedToggleFavouriteRef = useRef(
    async (specialist: ISpecialist | Specialist, operation: 'create' | 'delete') => {
      if (operation === 'delete') await dispatch(removeSpecialistFromFavourite(specialist));
      else await dispatch(addSpecialistToFavourite(specialist));
    }
  );

  const isFavouriteSpecialist = useCallback(
    (specialistID: string) => {
      if (!currentUser?.id) return false;

      const specialist = specialistList?.find((el) => el.id === specialistID);
      const favouriteSpecialist = favouriteSpecialistList?.find((el) => el.id === specialistID);

      return specialist?.favourite ?? !!favouriteSpecialist;
    },

    [favouriteSpecialistList, specialistList, currentUser?.id]
  );

  const toggleFavourite = useCallback(
    async (specialist: ISpecialist | Specialist, cb?: (action: 'delete' | 'create') => void) => {
      if (!currentUser?.id || !debouncedToggleFavouriteRef.current) return;
      const isNeedToDelete = currentUser?.favourite_specialist_ids?.includes(specialist.id);

      const action = isNeedToDelete ? 'delete' : 'create';
      debouncedToggleFavouriteRef.current(specialist, action);
      cb?.(action);
      isomorficHapticsImpact({ style: ImpactStyle.Light });
    },
    [currentUser?.id, specialistList, debouncedToggleFavouriteRef.current, currentUser?.favourite_specialist_ids]
  );

  return {
    isFavouriteSpecialist,
    toggleFavourite,
    loading: favouriteSpecialistListStatus !== 'succeeded',
  };
};
